

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboardStats,
  fetchDistrictUsers,
  fetchDistrictTaskSubmit,
} from "../redux/actions/dashboardActions";
import { FiUsers, FiClipboard, FiCalendar, FiTrendingUp } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#FF8042", "#FFBB28", "#0088FE", "#00C49F"];

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, districtUsers, districtTaskSubmit, loading } = useSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchDistrictUsers());
    dispatch(fetchDistrictTaskSubmit());
  }, [dispatch]);

  const districtData = districtUsers.map((item) => ({
    name: item.district,
    value: item.total_users,
  }));

  return (
    <div className="min-h-screen bg-gray-50 p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((_, index) => (
              <Skeleton key={index} height={120} borderRadius={12} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={<FiUsers className="text-2xl text-blue-600" />} label="Total Users" value={stats.total_users || 0} />
            <StatCard icon={<FiClipboard className="text-2xl text-green-600" />} label="Total Attendance" value={stats.total_attendance || 0} />
            <StatCard icon={<FiCalendar className="text-2xl text-purple-600" />} label="Total Leaves" value={stats.total_leaves || 0} />
            <StatCard icon={<FiTrendingUp className="text-2xl text-yellow-600" />} label="Total Task Submit" value={stats.total_task_submit || 0} />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Pie Chart with Scrollable Legend */}
          <div className="bg-white rounded-xl p-6 shadow-lg border max-h-[550px] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">District-wise User Distribution (Pie)</h2>
            {districtData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={districtData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} (${(percent * 100).toFixed(1)}%)`
                      }
                      outerRadius={130}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {districtData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>

                {/* Scrollable Legend */}
                <div className="mt-4 max-h-40 overflow-y-auto border-t pt-2">
                  {districtData.map((item, index) => (
                    <div key={index} className="flex justify-between py-1 text-sm">
                      <div className="flex items-center">
                        <span
                          className="inline-block w-3 h-3 mr-2 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></span>
                        {item.name}
                      </div>
                      <span>{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500">No Data Available</p>
            )}
          </div>

          {/* Horizontal Scroll Bar Chart for Task Submit */}
          <div className="bg-white rounded-xl p-6 shadow-lg border overflow-x-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">District-wise Task Submit (Bar)</h2>
            {districtTaskSubmit.length > 0 ? (
              <ResponsiveContainer width={districtTaskSubmit.length * 100} height={400}>
                <BarChart
                  data={districtTaskSubmit}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="district" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_users" fill="#82ca9d" name="Total Users" />
                  <Bar dataKey="total_task_submit" fill="#ffc658" name="Total Task Submit" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500">No Data Available</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
    <div className="flex items-center justify-between">
      <div className="bg-gray-100 p-4 rounded-lg">{icon}</div>
      <FiTrendingUp className="text-xl text-green-500" />
    </div>
    <h3 className="text-gray-500 text-sm mt-4">{label}</h3>
    <div className="flex items-baseline mt-2">
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

export default Dashboard;
